import type { TrackingEvent } from '../../types';
import {
    hasClientSpecificVariant,
    isLeadEvent,
} from '../../utils/conversionEvents';
import { getFacebookEvent, type FacebookEventData } from './utils';

/**
 * Prefix marking an event as belonging to the client whose ad the visitor
 * clicked, as opposed to any conversion anywhere on the site.
 */
const CLIENT_SPECIFIC_PREFIX = 'ClientSpecific_';

/**
 * Meta's standard lead event. Reported for every lead type so a campaign has one
 * signal covering all of them, which matters because an ad set needs roughly 50
 * conversions to leave the learning phase and no single lead type reaches that
 * on its own for most clients.
 */
const LEAD_EVENT = 'Lead';

/**
 * Every Meta event to report for one logged conversion.
 *
 * A conversion by someone who clicked a client's ad, and which belongs to that
 * same client, produces four: the conversion itself, its client-specific
 * variant, `Lead`, and `ClientSpecific_Lead`. A conversion with no ad behind it,
 * or one belonging to a different professional than the ad's, produces only the
 * site-wide pair.
 *
 * All of them share the caller's deduplication id. Meta deduplicates on the
 * event name and id together, so each name still collapses with its own
 * server-side copy while remaining distinct from the others.
 */
export function deriveMetaEvents(
    event: TrackingEvent,
    { clientMatched }: { clientMatched: boolean },
): FacebookEventData[] {
    const base = getFacebookEvent(event);
    if (!base) {
        return [];
    }

    const events: FacebookEventData[] = [base];
    const clientSpecific = clientMatched && hasClientSpecificVariant(event);

    if (clientSpecific) {
        events.push(clientSpecificVariant(base.eventName));
    }

    if (isLeadEvent(event)) {
        events.push({ eventName: LEAD_EVENT, type: 'standard' });

        if (clientSpecific) {
            events.push(clientSpecificVariant(LEAD_EVENT));
        }
    }

    return events;
}

/**
 * Client-specific events are always custom: the prefixed name is ours, so Meta
 * has no standard event to match it against.
 */
function clientSpecificVariant(eventName: string): FacebookEventData {
    return {
        eventName: `${CLIENT_SPECIFIC_PREFIX}${eventName}`,
        type: 'custom',
    };
}
